import React, { useState } from 'react';
import { ArrowIcon } from './ArrowIcon';
import { useIsFinePointer } from '../hooks/useIsFinePointer';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface CapabilityRowProps {
  number: string;
  title: string;
  desc: string;
}

export const CapabilityRow: React.FC<CapabilityRowProps> = ({ number, title, desc }) => {
  const [motion, setMotion] = useState({ dx: 0, dy: 0, hovering: false });
  const isFinePointer = useIsFinePointer();
  const { elementRef, isVisible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: '0px 0px -8% 0px',
  });

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Touch devices fire synthetic mousemove/mouseleave events on tap, which
    // would otherwise leave the title/arrow visibly displaced with no real
    // "leave" event to reset them — restrict the tilt effect to real mice.
    if (!isFinePointer) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = Math.max(-8, Math.min(8, (e.clientX - cx) * 0.05));
    const dy = Math.max(-8, Math.min(8, (e.clientY - cy) * 0.25));
    setMotion({ dx, dy, hovering: true });
  };

  const handleLeave = () => setMotion({ dx: 0, dy: 0, hovering: false });

  const boost = motion.hovering ? 8 : 0;

  return (
    <div
      ref={elementRef}
      className={`cap-row reveal ${isVisible ? 'is-visible' : ''}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      data-cursor-hover
    >
      <div className="cap-row__number">{number}</div>
      <div
        className="cap-row__title"
        style={{ transform: `translateX(${motion.hovering ? 8 : 0}px)` }}
      >
        {title}
      </div>
      <div className="cap-row__desc">{desc}</div>
      <div
        className="cap-row__arrow"
        style={{ transform: `translate(${motion.dx + boost}px, ${motion.dy}px)` }}
      >
        <ArrowIcon />
      </div>
    </div>
  );
};
